import {
  CSSProperties,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { buildDfdEmbedUrl } from 'common/dfd-embed-url';
import { useDfdIframeHandshake } from 'hooks/use-dfd-iframe-handshake.hook';

export interface DfdEmbedFrameProps {
  id: string;
  title: string;
  flowId?: string;
  widgetId?: string;
  style?: CSSProperties;
  /** Set by Genesis `styled(DfdEmbedFrame, …)`. */
  className?: string;
}

/** TELUS widgets draw edge to edge and leave the side margin to the page that hosts them. */
export const DFD_WIDGET_GUTTER = {
  boxSizing: 'border-box',
  paddingLeft: '$one',
  paddingRight: '$one',
} as const;

/** The TELUS embed, with the authentication handshake attached. */
export const DfdEmbedFrame = ({
  id,
  title,
  flowId,
  widgetId,
  style,
  className,
}: DfdEmbedFrameProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const builtSrc = useMemo(
    () => buildDfdEmbedUrl({ flowId, widgetId }),
    [flowId, widgetId],
  );

  // Keyed by the URL it replaced, so a change of flow or widget drops it without
  // a reset effect and without a render on the stale URL.
  const [navigated, setNavigated] = useState<{ from: string; to: string }>();
  const src = navigated?.from === builtSrc ? navigated.to : builtSrc;

  const { isListening } = useDfdIframeHandshake({
    iframeRef,
    src,
    surface: id,
    onNavigateRequest: (url) => setNavigated({ from: builtSrc, to: url }),
  });

  // The iframe announces readiness once, so it must not navigate before the
  // listener exists.
  useLayoutEffect(() => {
    if (isListening && iframeRef.current && iframeRef.current.src !== src) {
      iframeRef.current.src = src;
    }
  }, [isListening, src]);

  return (
    <iframe
      ref={iframeRef}
      id={id}
      title={title}
      allow="geolocation"
      height="100%"
      width="100%"
      style={style}
      className={className}
    />
  );
};
